import React, { useEffect, useState } from 'react';
import { getIconAspectRatio, getIconUrl } from '../../services/iconService';
import { forNotoEmoji } from './clueUtils';
import { CELL, cornerHits, customCorner, emojiCorner, emojiOverhang, symbolCorner } from './clueCorners';

// Mirrors _ClueIcons in the app's cw_clue_utils.dart, in units of the glyph size unless noted.
// Every Noto Emoji glyph's ink spans 1.016 em, centered in a line-height: 1 box.
const EMOJI_INK_HEIGHT = 1.016;
const GLYPH_GAP = 0.1;
const QUOTE_FONT = 0.5;
// Geologica's ” advances 0.44 em and its ink starts 0.14 em below the line top
const QUOTE_ADVANCE = 0.44;
const QUOTE_INK_TOP = 0.14;
// By default the modifier sits just outside the icons' bottom-right corner, tucked under their right edge by BADGE_INSET
const BADGE_FONT = 0.315;
const BADGE_INSET = 0.045;
// Its line box starts this many em above the icons' bottom, so its ink (0.15 to 0.85 em down) starts just below them
const BADGE_LIFT = 0.07;
const BADGE_INK_TOP = 0.15;
const BADGE_INK_BOTTOM = 0.85;
// With a corner map it may move up and left into empty space, in steps of a grid cell
const MAX_TUCK = 0.5;
// Room kept past the modifier's ink, and past the icons' ink on the other side, so neither sits flush against the edge of the space
const BADGE_EDGE_GAP = 0.06;
const DECORATION_FONT = "'Geologica', sans-serif";

let measureContext = null;
const badgeWidthEm = (text) => {
  measureContext = measureContext || document.createElement('canvas').getContext('2d');
  measureContext.font = `600 100px ${DECORATION_FONT}`;
  return measureContext.measureText(text).width / 100;
};
const MIN_DECORATION_FONT = 7;
// A custom icon of aspect ratio a gets a box of a^0.75 × a^-0.25: wide ones grow wide without
// thinning to a strip, and every shape keeps about the visual weight of a square glyph
const ASPECT_WIDTH_EXPONENT = 0.75;
const MAX_GLYPH_HEIGHT = 1.15;
// Wide emoji ink runs up to 0.115 past its box; with a modifier, several icons with an emoji among them keep that far from the left edge
const EMOJI_MOD_LEFT_PAD = 0.12;

// Small glyphs draw heavier: fully heavy at half the base size, normal from 0.8 of it
const weightBlend = (g, size) => Math.min(Math.max((g / size - 0.5) / 0.3, 0), 1);
const lerp = (heavy, normal, t) => heavy + (normal - heavy) * t;

const boxFor = (glyph, aspects) => {
  if (glyph.type !== 'custom') return { glyph, width: 1, height: 1 };
  const aspect = aspects[glyph.content] ?? 1;
  const height = Math.min(aspect ** (ASPECT_WIDTH_EXPONENT - 1), MAX_GLYPH_HEIGHT);
  return { glyph, width: height * aspect, height, measured: glyph.content in aspects };
};

const rowsOf = (boxes, cols) => {
  const rows = [];
  for (let start = 0; start < boxes.length; start += cols) rows.push(boxes.slice(start, start + cols));
  return rows;
};

const gridUnits = (boxes, cols) => {
  const rows = rowsOf(boxes, cols);
  const width = Math.max(...rows.map(row => row.reduce((sum, b) => sum + b.width, 0) + (row.length - 1) * GLYPH_GAP));
  const height = rows.reduce((sum, row) => sum + Math.max(...row.map(b => b.height)), 0) + (rows.length - 1) * GLYPH_GAP;
  return [width, height];
};

const useIconAspects = (glyphs) => {
  const names = glyphs.filter(g => g.type === 'custom').map(g => g.content).join(',');
  const [aspects, setAspects] = useState({});

  useEffect(() => {
    let live = true;
    const list = names ? names.split(',') : [];
    Promise.all(list.map(getIconAspectRatio)).then(ratios => {
      if (live) setAspects(Object.fromEntries(list.map((name, i) => [name, ratios[i]]).filter(([, r]) => r)));
    });
    return () => { live = false; };
  }, [names]);

  return aspects;
};

const CustomGlyph = ({ name, width, height, imageFilter }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let live = true;
    getIconUrl(name).then(u => live && setUrl(u)).catch(() => live && setUrl(null));
    return () => { live = false; };
  }, [name]);

  if (!url) {
    return <span style={{ fontSize: `${height * 0.3}px`, whiteSpace: 'nowrap', overflow: 'hidden' }}>[{name}]</span>;
  }
  return <img src={url} alt={name} style={{ width: `${width}px`, height: `${height}px`, objectFit: 'contain', filter: imageFilter, display: 'block' }} />;
};

const Glyph = ({ box: { glyph, width, height }, g, weight, imageFilter }) => {
  const box = {
    width: `${width * g}px`,
    height: `${height * g}px`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (glyph.type === 'custom') {
    return <div style={box}><CustomGlyph name={glyph.content} width={width * g} height={height * g} imageFilter={imageFilter} /></div>;
  }

  if (glyph.type === 'symbol') {
    return (
      <div style={box}>
        <span
          className="material-symbols-rounded"
          style={{
            fontSize: `${g * 1.3}px`,
            fontVariationSettings: `'FILL' 0, 'wght' ${lerp(350, 200, weight)}, 'GRAD' 0, 'opsz' ${lerp(20, 40, weight)}`,
            lineHeight: 1,
            display: 'block',
          }}
        >
          {glyph.content}
        </span>
      </div>
    );
  }

  return (
    <div style={box}>
      <span style={{
        fontFamily: "'Noto Emoji', sans-serif",
        fontSize: `${g / EMOJI_INK_HEIGHT}px`,
        fontVariationSettings: `'wght' ${lerp(700, 400, weight)}`,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        display: 'block',
      }}>
        {forNotoEmoji(glyph.content)}
      </span>
    </div>
  );
};

// A modifier beside a single icon tucks into its corner once that corner's map is ready
const useCorner = (parsed, boxes) => {
  const box = parsed.modifier && boxes.length === 1 ? boxes[0] : null;
  const type = box?.glyph.type;
  const content = box?.glyph.content;
  const sync = type === 'emoji' ? emojiCorner(content) : type === 'symbol' ? symbolCorner(content) : null;
  // A custom icon's box depends on its aspect ratio, so wait for that before measuring
  const customKey = type === 'custom' && box.measured ? `${content}|${box.width}|${box.height}` : null;
  const [custom, setCustom] = useState(null);

  useEffect(() => {
    setCustom(null);
    if (!customKey) return undefined;
    let live = true;
    const [name, width, height] = customKey.split('|');
    customCorner(name, Number(width), Number(height)).then(bits => live && setCustom(bits));
    return () => { live = false; };
  }, [customKey]);

  return type === 'custom' ? custom : sync;
};

// Icons fitted to maxWidth × maxHeight: each glyph sits in a box with its ink
// centered (square, or shaped like a custom icon), several glyphs wrap into
// whichever grid lets them be largest, and the quotes and modifier are placed
// inside the bounds, not past them. A modifier beside a single icon moves into
// empty space in the icon's corner when that lets the icon be larger or stay centered.
const ClueIcons = ({ parsed, size, sizeMultiplier = 1, maxWidth = Infinity, maxHeight, imageFilter = 'brightness(0)' }) => {
  const aspects = useIconAspects(parsed.glyphs);
  const boxes = parsed.glyphs.map(glyph => boxFor(glyph, aspects));
  const corner = useCorner(parsed, boxes);
  const n = boxes.length;
  const hasMod = !!parsed.modifier;
  const emoji = parsed.glyphs.filter(glyph => glyph.type === 'emoji');
  const emojiLeftPad = !emoji.length ? 0 : parsed.glyphs.length > 1 ? EMOJI_MOD_LEFT_PAD : emojiOverhang(emoji[0].content);
  const quoteFont = (g) => Math.max(g * QUOTE_FONT, MIN_DECORATION_FONT);
  const badgeFont = (g) => Math.max(g * BADGE_FONT, MIN_DECORATION_FONT);
  const sideOf = (g) => (parsed.quoted ? quoteFont(g) * QUOTE_ADVANCE : 0);
  const badgeEm = hasMod ? badgeWidthEm(parsed.modifier) : 0;

  // The modifier's ink, relative to the icons' bottom-right corner, in units of g
  const badgeInk = (g, tuckX, tuckY) => {
    const b = badgeFont(g) / g;
    const x0 = -BADGE_INSET - tuckX;
    return { x0, y0: (BADGE_INK_TOP - BADGE_LIFT) * b - tuckY, x1: x0 + badgeEm * b, y1: (BADGE_INK_BOTTOM - BADGE_LIFT) * b - tuckY };
  };

  // Where the modifier may go, smallest moves first
  const tucks = (g) => {
    const out = [[0, 0]];
    if (!corner) return out;
    const steps = Math.round(MAX_TUCK / CELL);
    for (let total = 1; total <= steps * 2; total++) {
      for (let x = Math.max(0, total - steps); x <= Math.min(total, steps); x++) {
        const tuckX = x * CELL, tuckY = (total - x) * CELL;
        const ink = badgeInk(g, tuckX, tuckY);
        if (!cornerHits(corner, ink.x0, ink.y0, ink.x1, ink.y1)) out.push([tuckX, tuckY]);
      }
    }
    return out;
  };

  // How far past the icons' right and bottom edges the modifier reaches, in pixels
  const overhang = (g, tuckX, tuckY) => {
    if (!hasMod) return [0, 0];
    const ink = badgeInk(g, tuckX, tuckY);
    return [Math.max(ink.x1 + BADGE_EDGE_GAP, 0) * g, Math.max(ink.y1 + BADGE_EDGE_GAP, 0) * g];
  };

  const maxGlyphOf = (reach, drop) => size * sizeMultiplier * (hasMod && (reach > 0 || drop > 0) ? 0.95 : parsed.quoted ? 0.9 : 1);
  const leftOf = (g, reach) => Math.max(sideOf(g), reach > 0 ? g * (emojiLeftPad + BADGE_EDGE_GAP) : 0);

  const fit = (guess) => {
    let best = { g: 0, rows: 1, tuckX: 0, tuckY: 0 };
    let bestOverhang = Infinity;
    for (const [tuckX, tuckY] of tucks(guess)) {
      const [reach, drop] = overhang(guess, tuckX, tuckY);
      const maxGlyph = maxGlyphOf(reach, drop);
      const extraWidth = leftOf(guess, reach) + Math.max(sideOf(guess), reach);
      const height = maxHeight ?? maxGlyph + drop;
      for (let rows = 1; rows <= n; rows++) {
        const [widthUnits, heightUnits] = gridUnits(boxes, Math.ceil(n / rows));
        const g = Math.min(maxGlyph, (maxWidth - extraWidth) / widthUnits, (height - drop) / heightUnits);
        // Larger icons win; at the same size, a modifier that sticks out less keeps the icon more centered
        const larger = g > best.g * 1.005 + 0.01;
        const asLarge = g > best.g * 0.995 - 0.01;
        if (larger || (asLarge && reach + drop < bestOverhang - 0.01)) {
          best = { g, rows, tuckX, tuckY };
          bestOverhang = reach + drop;
        }
      }
    }
    return best;
  };

  // The modifier's size scales with the icons, so a guess settles after a few rounds, and any overshoot left is then shrunk away
  const extent = ({ g, rows, tuckX, tuckY }) => {
    const [widthUnits, heightUnits] = gridUnits(boxes, Math.ceil(n / rows));
    const [reach, drop] = overhang(g, tuckX, tuckY);
    return [leftOf(g, reach) + widthUnits * g + Math.max(sideOf(g), reach), heightUnits * g + drop];
  };
  let result = fit(size * sizeMultiplier);
  for (let i = 0; i < 4; i++) result = fit(result.g);
  for (let i = 0; i < 6 && result.g > 1; i++) {
    const [width, height] = extent(result);
    const scale = Math.min(maxWidth / width, (maxHeight ?? Infinity) / height);
    if (scale >= 0.999) break;
    result = { ...result, g: result.g * scale };
  }
  const g = Math.max(result.g, 1);
  const cols = Math.ceil(n / result.rows);
  const [widthUnits, heightUnits] = gridUnits(boxes, cols);
  const gap = g * GLYPH_GAP;
  const weight = weightBlend(g, size);

  const grid = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${gap}px` }}>
      {rowsOf(boxes, cols).map((row, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: `${gap}px` }}>
          {row.map((box, j) => <Glyph key={j} box={box} g={g} weight={weight} imageFilter={imageFilter} />)}
        </div>
      ))}
    </div>
  );
  if (!hasMod && !parsed.quoted) return grid;

  const [reach, drop] = overhang(g, result.tuckX, result.tuckY);
  const side = sideOf(g);
  const left = leftOf(g, reach);
  const gridWidth = widthUnits * g;
  const gridHeight = heightUnits * g;
  const decoration = { position: 'absolute', fontFamily: DECORATION_FONT, lineHeight: 1, whiteSpace: 'nowrap' };
  const quote = { ...decoration, top: `${-quoteFont(g) * QUOTE_INK_TOP}px`, fontSize: `${quoteFont(g)}px` };

  return (
    <div style={{
      position: 'relative',
      flexShrink: 0,
      width: `${left + gridWidth + Math.max(side, reach)}px`,
      height: `${gridHeight + drop}px`,
    }}>
      <div style={{ position: 'absolute', left: `${left}px`, top: 0 }}>{grid}</div>
      {parsed.quoted && (
        <>
          <span style={{ ...quote, left: 0 }}>”</span>
          <span style={{ ...quote, right: 0 }}>”</span>
        </>
      )}
      {hasMod && (
        <span style={{
          ...decoration,
          left: `${left + gridWidth - g * (BADGE_INSET + result.tuckX)}px`,
          top: `${gridHeight - badgeFont(g) * BADGE_LIFT - g * result.tuckY}px`,
          fontSize: `${badgeFont(g)}px`,
          fontWeight: 550,
        }}>
          {parsed.modifier}
        </span>
      )}
    </div>
  );
};

export default ClueIcons;
